import { useCallback, useEffect, useRef, useState } from 'react';

const SEARCH_DEBOUNCE_MS = 300;

const useAccountSearch = (fetchPage, isOpen) => {
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const hasLoaded = useRef(false);

  const queryRef = useRef('');

  const requestId = useRef(0);

  const loadPage = useCallback(
    async ({ append, query, offset }) => {
      const id = requestId.current + 1;
      requestId.current = id;
      queryRef.current = query;
      setIsLoadingAccounts(true);

      try {
        const { accounts, total } = await fetchPage(query, offset);
        if (requestId.current !== id) return;

        setAccountOptions((current) => {
          if (!append) return accounts;

          const seen = new Set(current.map((option) => String(option.value)));
          return [
            ...current,
            ...accounts.filter((option) => !seen.has(String(option.value))),
          ];
        });
        setAccountsTotal(total);
      } catch {
        if (requestId.current === id && !append) {
          setAccountOptions([]);
          setAccountsTotal(0);
        }
      } finally {
        if (requestId.current === id) setIsLoadingAccounts(false);
      }
    },
    [fetchPage],
  );

  useEffect(() => {
    if (!isOpen || hasLoaded.current) return undefined;

    hasLoaded.current = true;
    let cancelled = false;
    let settled = false;
    setIsLoadingAccounts(true);

    fetchPage('', 0)
      .then((firstPage) => {
        settled = true;
        if (cancelled) return;
        setAccountOptions(firstPage.accounts);
        setAccountsTotal(firstPage.total);
      })
      .catch(() => {
        settled = true;
        if (cancelled) return;
        // A failed fresh load empties the menu, same as a failed search —
        // see loadPage's own non-append branch.
        hasLoaded.current = false;
        setAccountOptions([]);
        setAccountsTotal(0);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAccounts(false);
      });

    return () => {
      cancelled = true;
      setIsLoadingAccounts(false);
      if (!settled) hasLoaded.current = false;
    };
  }, [fetchPage, isOpen]);

  const searchTimer = useRef(null);

  useEffect(() => {
    if (!isOpen && searchTimer.current) clearTimeout(searchTimer.current);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [isOpen]);

  const searchAccounts = useCallback(
    (query) => {
      if (searchTimer.current) clearTimeout(searchTimer.current);

      searchTimer.current = setTimeout(() => {
        loadPage({ append: false, query, offset: 0 });
      }, SEARCH_DEBOUNCE_MS);
    },
    [loadPage],
  );

  const hasMoreAccounts = accountOptions.length < accountsTotal;

  const loadMoreAccounts = useCallback(() => {
    if (isLoadingAccounts) return;
    if (accountOptions.length >= accountsTotal) return;

    loadPage({
      append: true,
      query: queryRef.current,
      offset: accountOptions.length,
    });
  }, [accountOptions.length, accountsTotal, isLoadingAccounts, loadPage]);

  return {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    loadMoreAccounts,
    searchAccounts,
  };
};

export default useAccountSearch;
